import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { get } from "lodash";
import { useEffect, useMemo, useState, useCallback } from "react";

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  Stack,
  Divider
} from "@mui/material";

import { Line } from "react-chartjs-2";

import "../css/font-awesome-pro.css";
import { energyChart, keycutChart, getKeyCutData } from "../api";
import BackButton from "./Components/BackButton";

/* ================= CHART OPTIONS ================= */

const chartOptions = {
  responsive: true,
  plugins: {
    legend: { position: "top" }
  }
};

/* ================= MAIN ================= */

export default function DeviceDetails2() {
  const navigate = useNavigate();
  const { serialNumber } = useParams();

  /* ================= STATE ================= */
  const [keycutData, setKeycutData] = useState([]);
  const [ecData, setECData] = useState({ labels: [], datasets: [] });
  const [kcData, setKCData] = useState({ labels: [], datasets: [] });

  /* ---------- TABLE STATE ---------- */
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  /* ================= FETCHERS ================= */

  const fetchEnergyChart = useCallback(
    (e) => {
      const range = get(e, "target.value", "7days");

      energyChart(serialNumber, range).then((resp) => {
        setECData({
          labels: resp.labels,
          datasets: [
            {
              label: "Energy",
              data: resp.data,
              borderColor: "#ef4444",
              backgroundColor: "rgba(239,68,68,0.4)"
            }
          ]
        });
      });
    },
    [serialNumber]
  );

  const fetchKeycutChart = useCallback(
    (e) => {
      const range = get(e, "target.value", "7days");

      keycutChart(serialNumber, range).then((resp) => {
        setKCData({
          labels: resp.labels,
          datasets: [
            dataset("Total Key Cut", resp.data.map(d => d.keycount), "#ef4444"),
            dataset("Flat Cut", resp.data.map(d => d.flatcut), "#3b82f6"),
            dataset("Single Side", resp.data.map(d => d.singleside), "#10b981"),
            dataset("Double Side", resp.data.map(d => d.doubleside), "#f59e0b"),
            dataset("Bit Cut", resp.data.map(d => d.bitcut), "#8b5cf6")
          ]
        });
      });
    },
    [serialNumber]
  );

  /* ================= AUTH + INITIAL LOAD ================= */

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    const isAdmin = sessionStorage.getItem("isAdmin") === "true";
    if (isAdmin && !serialNumber) {
      navigate("list", { replace: true });
      return;
    }

    fetchEnergyChart();
    fetchKeycutChart();
    getKeyCutData(serialNumber).then(setKeycutData);

  }, [
    navigate,
    serialNumber,
    fetchEnergyChart,
    fetchKeycutChart
  ]);

  /* ================= TABLE LOGIC ================= */

  const filteredData = useMemo(() => {
    return keycutData.filter(d =>
      moment(d.date)
        .format("DD MMM YY")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [keycutData, search]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  return (
    <Box sx={{ maxWidth: 1400, mx: "auto" }}>
      <BackButton />

         {serialNumber && (
                <Typography variant="body2" color="text.secondary">
                  Serial Number:&nbsp;
                  <a
                    href={`/#/home/device/${serialNumber}`}
                    style={{
                      color: "#2563eb",
                      fontWeight: 600,
                      textDecoration: "none"
                    }}
                    onMouseOver={(e) => (e.target.style.textDecoration = "underline")}
                    onMouseOut={(e) => (e.target.style.textDecoration = "none")}
                  >
                    {serialNumber}
                  </a>
                </Typography>
              )}
      <Typography variant="h5" fontWeight={800} mb={2}>
        Device Analytics
      </Typography>

      <Grid container spacing={2}>
        {/* ================= CHARTS ================= */}
        <Grid item xs={12} md={6}>
          <ChartCard title="Key Count Line Chart" onChange={fetchKeycutChart} data={kcData} />
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard title="Energy Unit Line Chart" onChange={fetchEnergyChart} data={ecData} />
        </Grid>

        {/* ================= TABLE ================= */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700}>
                Device Data
              </Typography>

              {/* CONTROLS */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 my-4">
                <div className="flex items-center gap-2 text-sm">
                  <span>Show</span>
                  <select
                    className="border rounded px-2 py-1"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    {[10, 25, 50, 100].map(n => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <span>entries</span>
                </div>

                <input
                  type="search"
                  placeholder="Search by date..."
                  className="border rounded px-3 py-2 text-sm w-full md:w-64"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>

              <Divider sx={{ mb: 2 }} />

              {/* TABLE */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-300">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border px-3 py-2">#</th>
                      <th className="border px-3 py-2">Date</th>
                      <th className="border px-3 py-2">Key</th>
                      <th className="border px-3 py-2">Flat</th>
                      <th className="border px-3 py-2">Bit</th>
                      <th className="border px-3 py-2">Single</th>
                      <th className="border px-3 py-2">Double</th>
                      <th className="border px-3 py-2">Unit</th>
                      <th className="border px-3 py-2">Runtime</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.map((d, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="border px-3 py-2">{(page - 1) * pageSize + i + 1}</td>
                        <td className="border px-3 py-2">{moment(d.date).format("DD MMM YY")}</td>
                        <td className="border px-3 py-2">{d.v.keycount}</td>
                        <td className="border px-3 py-2">{d.v.flatcut}</td>
                        <td className="border px-3 py-2">{d.v.bitcut}</td>
                        <td className="border px-3 py-2">{d.v.singleside}</td>
                        <td className="border px-3 py-2">{d.v.doubleside}</td>
                        <td className="border px-3 py-2">{parseInt(d.v.energy) / 1000}</td>
                        <td className="border px-3 py-2">{d.v.runtime}</td>
                      </tr>
                    ))}

                    {paginatedData.length === 0 && (
                      <tr>
                        <td colSpan={9} className="text-center py-6 text-gray-500">
                          No data found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              <div className="flex items-center justify-between mt-4 text-sm">
                <span>
                  Showing {(page - 1) * pageSize + 1}–
                  {Math.min(page * pageSize, filteredData.length)} of{" "}
                  {filteredData.length}
                </span>

                <div className="flex gap-2">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <button
                    disabled={page === totalPages || totalPages === 0}
                    onClick={() => setPage(p => p + 1)}
                    className="px-3 py-1 border rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

/* ================= HELPERS ================= */

function ChartCard({ title, data, onChange }) {
  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" mb={1}>
          <Typography fontWeight={700}>{title}</Typography>
          <Select size="small" defaultValue="7days" onChange={onChange}>
            <MenuItem value="7days">7 Days</MenuItem>
            <MenuItem value="30days">30 Days</MenuItem>
            <MenuItem value="6month">6 Months</MenuItem>
          </Select>
        </Stack>
        <Line data={data} options={chartOptions} />
      </CardContent>
    </Card>
  );
}

function dataset(label, data, color) {
  return {
    label,
    data,
    borderColor: color,
    backgroundColor: `${color}66`,
    tension: 0.3
  };
}
